import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiBell } from 'react-icons/fi';
import './NotificationBell.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const BellIcon = FiBell as unknown as React.FC<{ 'aria-hidden'?: boolean }>;

type InviteState = 'pending' | 'accepted' | 'expired' | 'revoked';

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string | null;
  data: { memberId?: string; projectId?: string; expiresAt?: string | null };
  read: boolean;
  createdAt: string;
  inviteState?: InviteState;
}

const SHARE_INVITE = 'project_share_invite';

const timeAgo = (iso: string) => {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

/**
 * Header bell: generic notification centre. Share invites are the first type; other types render
 * as plain title/body rows. Loads on mount, on window focus and when opened — no polling.
 */
const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/me/notifications`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setItems(data.items || []);
      setUnread(data.unreadCount || 0);
    } catch { /* the bell just stays as it was */ }
  }, []);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    setOpen(prev => !prev);
    if (!open) load();
  };

  const post = (path: string) => fetch(`${API_BASE}${path}`, { method: 'POST', credentials: 'include' });

  const markRead = async (n: NotificationItem) => {
    if (n.read) return;
    await post(`/api/me/notifications/${n.id}/read`).catch(() => undefined);
    load();
  };

  const markAllRead = async () => {
    await post('/api/me/notifications/read-all').catch(() => undefined);
    load();
  };

  const accept = async (n: NotificationItem) => {
    if (!n.data.memberId) return;
    setBusyId(n.id);
    setError('');
    try {
      const res = await post(`/api/me/share-invites/${n.data.memberId}/accept`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Could not accept the invite');
      }
      await load();
    } catch (e: any) {
      setError(e.message || 'Could not accept the invite');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="notif-wrap" ref={wrapRef}>
      <button
        type="button"
        className="notif-bell"
        onClick={toggle}
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <BellIcon aria-hidden={true} />
        {unread > 0 && <span className="notif-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <div className="notif-panel" role="dialog" aria-label="Notifications">
          <div className="notif-head">
            <span className="notif-title">Notifications</span>
            {unread > 0 && <button type="button" className="notif-link" onClick={markAllRead}>Mark all read</button>}
            <button type="button" className="notif-close" onClick={() => setOpen(false)} aria-label="Close notifications">✕</button>
          </div>
          {error && <div className="notif-error" role="alert">{error}</div>}
          {items.length === 0 ? (
            <div className="notif-empty">You’re all caught up.</div>
          ) : (
            <ul className="notif-list">
              {items.map(n => {
                const isInvite = n.type === SHARE_INVITE;
                return (
                  <li key={n.id} className={`notif-item ${n.read ? '' : 'unread'}`} onClick={() => markRead(n)}>
                    <div className="notif-main">
                      <span className="notif-item-title">{n.title}</span>
                      {n.body && <span className="notif-item-body">{n.body}{isInvite && n.data.expiresAt ? ` · until ${new Date(n.data.expiresAt).toLocaleDateString()}` : ''}</span>}
                      <span className="notif-time">{timeAgo(n.createdAt)}</span>
                    </div>
                    {isInvite && (
                      <div className="notif-actions">
                        {n.inviteState === 'pending' && (
                          <button type="button" className="btn-sm btn-sm-accent" disabled={busyId === n.id}
                            onClick={e => { e.stopPropagation(); accept(n); }}>
                            {busyId === n.id ? 'Accepting…' : 'Accept'}
                          </button>
                        )}
                        {n.inviteState === 'accepted' && n.data.projectId && (
                          <Link className="btn-sm" to={`/projects/${n.data.projectId}`} onClick={() => setOpen(false)}>Open</Link>
                        )}
                        {n.inviteState === 'expired' && <span className="notif-state">Expired</span>}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
